import React, { useEffect } from "react";
import {
  BrowserRouter,
  Route,
  Routes,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import store from "../../store";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Login from "../../views/pages/Authentication/Login";
import Register from "../../views/pages/Authentication/Register";
import Roles from "../../views/pages/Role";
import Error404 from "../../views/pages/Error/Error404";
import JobList from "../../views/pages/Authentication/JobList";
import { AuthProvider, useAuth } from "../../AuthContext";
import { hasModule, moduleForPath, firstAllowedRoute } from "../../utils/permissions";
import Toasts from "../../views/pages/Ui_Interface/Components/Toats";
import Employee from "../../views/pages/Employee";
import Users from "../../views/pages/Users";
import ForgotPassword from "../../views/pages/Authentication/ForgotPassword";
import Attendance from "../../views/pages/Attendance";
import Payroll from "../../views/pages/Payroll";
import TDS from "../../views/pages/TDS";
import Arrears from "../../views/pages/Arrears";
import Incentive from "../../views/pages/Incentive";
import Penalty from "../../views/pages/Penalty";
import EmployeerComponent from "../../views/pages/EmployeerComponent";
import TrackSheet from "../../views/pages/TrackSheet";
import Reimbursement from "../../views/pages/Reimbursement";
import SalaryView from "../../views/pages/Employee/SalaryView";
import EmployeeData from "../../views/pages/EmployeeData/EmployeeData";
import EmployeeBankDetails from "../../views/pages/EmployeeBankDetails";
import Bonus from "../../views/pages/Bonus";
import EarnedLeave from "../../views/pages/EarnedLeave";
import EsicPf from "../../views/pages/Esic_PF";
import Report from "../../views/pages/Report";
import FolderList from "../../views/pages/Report/FolderList";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const RoleBasedRoute = ({ children }) => {
  const location = useLocation();
  const type = localStorage.getItem("type");

  if (!type) {
    return <Navigate to="/login" />;
  }

  if (type === "Admin" && location.pathname === "/role") {
    return <Navigate to="/employee" />;
  }

  const employeeAccessibleRoutes = ["/employee-data", `/salary-view/:id`];

  if (type === "Employee") {
    const isAccessible = employeeAccessibleRoutes.some((route) =>
      location.pathname.startsWith(route.replace(":id", ""))
    );

    if (!isAccessible) {
      return <Navigate to="/employee-data" />;
    }

    return children;
  }

  // Module-based gating for all other roles (Super Admin / Admin pass via hasModule).
  const requiredModule = moduleForPath(location.pathname);
  if (requiredModule && !hasModule(requiredModule)) {
    return <Navigate to={firstAllowedRoute()} />;
  }

  return children;
};

const AuthWrapper = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const publicRoutes = ["/login", "/forgot-password"];

  if (!isAuthenticated) {
    if (!publicRoutes.includes(location.pathname)) {
      return <Navigate to="/login" />;
    }
  } else {
    if (location.pathname === "/" || location.pathname === "*") {
      return <Navigate to="/role" />;
    }
  }

  return children;
};

const AppRouter = () => {
  return (
    <AuthProvider>
      <Provider store={store}>
        <BrowserRouter basename="/">
          <ScrollToTop />
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar
          />
          <Routes>
            {/* Public Routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />

            {/* Protected Routes Wrapped in AuthWrapper and RoleBasedRoute */}
            <Route
              path="*"
              element={
                <AuthWrapper>
                  <RoleBasedRoute>
                    <Routes>
                      <Route path="/role" element={<Roles />} />
                      <Route path="/employee" element={<Employee />} />
                      <Route path="/user" element={<Users />} />
                      <Route path="/attendance" element={<Attendance />} />
                      <Route path="/payroll" element={<Payroll />} />
                      <Route path="/tds" element={<TDS />} />
                      <Route path="/arrear" element={<Arrears />} />
                      <Route path="/incentive" element={<Incentive />} />
                      <Route path="/penalty" element={<Penalty />} />
                      <Route path="/job-list" element={<JobList />} />
                      <Route path="/toastr" element={<Toasts />} />
                      <Route path="/salary-view/:id" element={<SalaryView />} />
                      <Route
                        path="/employeer-component"
                        element={<EmployeerComponent />}
                      />
                      <Route path="/track-sheet" element={<TrackSheet />} />
                      <Route path="/reimbursement" element={<Reimbursement />} />
                      <Route path="/employee-data" element={<EmployeeData />} />
                      <Route path="/employee-bank-details" element={<EmployeeBankDetails />} />
                      <Route path="/bonus" element={<Bonus />} />
                      <Route path="/earned-leave" element={<EarnedLeave />} />
                      <Route path ="/esic-pf" element={<EsicPf />}/>
                      <Route path ="/report" element={<Report />}/>
                      <Route path ="/folder-list" element={<FolderList />}/>
                      <Route path="*" element={<Error404 />} />
                    </Routes>
                  </RoleBasedRoute>
                </AuthWrapper>
              }
            />

            {/* Fallback for unmatched routes */}
            <Route path="*" element={<Error404 />} />
          </Routes>
        </BrowserRouter>
      </Provider>
    </AuthProvider>
  );
};

export default AppRouter;
